import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Constants } from '@shared/constant/constants';

// Services
import { CookieManagerService } from './cookie-manager.service';

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService {
  public signupData: any;
  public updateAPreferences$: Subject<string> = new Subject();
  public updateToolTipPreferences$: Subject<boolean> = new Subject();
  private SchoolAdminLoggedIn = new Subject<boolean>();
  private SiteAdminLoggedIn = new Subject<boolean>();
  private StudentLoggedIn = new Subject<boolean>();
  constructor(private cookieManager: CookieManagerService) {
    this.SiteAdminLoggedIn.next(false);
  }

  private extractUserIdFromObject(payload: any): string {
    if (!payload || typeof payload !== 'object') return '';
    const candidates = [
      payload.id,
      payload._id,
      payload.userId,
      payload.sub,
      payload.user?.id,
      payload.user?._id,
      payload.data?.user?.id,
      payload.data?.user?._id,
      payload.profile?.id,
      payload.profile?._id,
    ];
    return (candidates.find((v: any) => typeof v === 'string' && v.trim().length > 0) as string) || '';
  }

  private extractUserIdFromToken(rawToken: string): string {
    try {
      const token = rawToken.startsWith('Bearer ') ? rawToken.slice(7) : rawToken;
      const parts = token.split('.');
      if (parts.length >= 2) {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(base64);
        const payload = JSON.parse(json);
        return this.extractUserIdFromObject(payload);
      }
    } catch (err) {
      /* ignore */
    }
    return '';
  }

  public createSession = (token: string, roleId: any, userId?: any, levelId?: any, levelNo?: any, userEmail?: any) => {
    this.cookieManager.setCookie('token', token);
    this.cookieManager.setCookie('roleId', roleId);

    let finalUserId = '';
    if (userId) {
      finalUserId = String(userId);
    } else {
      // try from token if not provided
      finalUserId = this.extractUserIdFromToken(token);
      if (!finalUserId) {
        // try from loggedInData if already set earlier
        const loggedData = this.getLoggedInData();
        finalUserId = this.extractUserIdFromObject(loggedData);
      }
    }
    if (finalUserId) {
      this.cookieManager.setCookie('userId', finalUserId);
      try {
        localStorage.setItem('userId', finalUserId);
      } catch (e) {
        console.warn('SessionManager: Failed to save userId to localStorage:', e);
      }
    }

    this.cookieManager.setCookie('mathLevelId', levelId);
    this.cookieManager.setCookie('levelNo', levelNo ? levelNo : '1');
    this.cookieManager.setCookie('email', userEmail);
  };

  public isSession = () => {
    return this.getToken() ? true : false;
  };

  public getToken = () => {
    return this.cookieManager.getCookie('token');
  };

  public getRole = () => {
    return this.cookieManager.getCookie('roleId');
  };

  public getUserId = () => {
    const idFromCookie = this.cookieManager.getCookie('userId');
    if (idFromCookie) return idFromCookie;

    // Fallback 0: localStorage cache
    try {
      const lsId = localStorage.getItem('userId');
      if (lsId) {
        this.cookieManager.setCookie('userId', lsId);
        return lsId;
      }
    } catch (e) {
      console.warn('SessionManager: localStorage access failed:', e);
    }

    // Fallback 1: from stored login payload
    const logged = this.getLoggedInData();
    const fromLogged = this.extractUserIdFromObject(logged);
    if (fromLogged) {
      this.cookieManager.setCookie('userId', fromLogged);
      try {
        localStorage.setItem('userId', fromLogged);
      } catch (e) {
        /* ignore */
      }
      return fromLogged;
    }

    // Fallback 2: decode JWT payload (supporting Bearer prefix)
    const token = this.getToken();
    if (token) {
      const fromJwt = this.extractUserIdFromToken(token);
      if (fromJwt) {
        this.cookieManager.setCookie('userId', fromJwt);
        try {
          localStorage.setItem('userId', fromJwt);
        } catch (e) {
          /* ignore */
        }
        return fromJwt;
      }
    }

    return '';
  };

  public getUserMathLevelId = () => {
    const mathLevelId = this.cookieManager.getCookie('mathLevelId');
    const matheLevelNo = this.cookieManager.getCookie('levelNo');
    return mathLevelId === 'null' || mathLevelId === 'undefined'
      ? {
          mathLevelId: '',
          matheLevelNo,
        }
      : { mathLevelId, matheLevelNo };
  };

  public removeCookies() {
    this.cookieManager.clearAllCookies();
  }

  public getTokenAndRole = () => {
    return { token: this.cookieManager.getCookie('token'), roleId: this.cookieManager.getCookie('roleId') };
  };

  public setUserMathLevelId = (id: string, no: string) => {
    this.cookieManager.setCookie('mathLevelId', id);
    this.cookieManager.setCookie('levelNo', no);
  };
  public setUserPreferences = (data: any) => {
    this.cookieManager.setCookie('preferences', JSON.stringify(data));
  };
  public getUserPreferences = () => {
    return this.cookieManager.getCookie('preferences') ? JSON.parse(this.cookieManager.getCookie('preferences')) : null;
  };

  public getAccessToken = () => {
    return this.cookieManager.getCookie('accessToken');
  };

  public setAccessToken = (accessToken: string) => {
    return this.cookieManager.setCookie('accessToken', accessToken);
  };

  public setFileIdAndBFileId = (data: any) => {
    this.cookieManager.setCookie('fileIdAndBFileId', JSON.stringify(data));
  };
  public getFileIdAndBFileId = () => {
    return this.cookieManager.getCookie('fileIdAndBFileId') ? JSON.parse(this.cookieManager.getCookie('fileIdAndBFileId')) : null;
  };
  public deleteFileIdAndBFileId = () => {
    return this.cookieManager.clearCookie('fileIdAndBFileId');
  };

  // Google Drive/Classroom File Handling
  public setGoogleFileId = (data: any) => {
    this.cookieManager.setCookie('googleFileId', JSON.stringify(data));
  };
  public getGoogleFileId = () => {
    return this.cookieManager.getCookie('googleFileId') ? JSON.parse(this.cookieManager.getCookie('googleFileId')) : null;
  };
  // Clear GoogleFile ID
  public clearGoogleFileId = () => {
    return this.cookieManager.clearCookie('googleFileId');
  };

  logout() {
    localStorage.removeItem('loggedInData');
    // The Google token lives in a cookie but its expiry lives here, so clearing only cookies left
    // half the token state behind — and it outlived a cache clear.
    localStorage.removeItem('lastGeneratedAccessTokenExpires');
    this.removeCookies();
  }

  public setFirebaseRefreshToken = (data: string) => {
    this.cookieManager.setCookie('setFirebaseRefreshToken', data);
  };

  public getFirebaseRefreshToken = () => {
    return this.cookieManager.getCookie('getFirebaseRefreshToken');
  };

  public getGoogleFirebaseRefreshToken = () => {
    return this.cookieManager.getCookie('setFirebaseRefreshToken');
  };

  public setFirebaseAccessTokenExpirationTime = (data: string) => {
    this.cookieManager.setCookie('firebaseAccessTokenExpirationTime', data);
  };

  public getFirebaseAccessTokenExpirationTime = () => {
    return this.cookieManager.getCookie('firebaseAccessTokenExpirationTime');
  };

  public setFirebaseUserId(id: string) {
    this.cookieManager.setCookie('setFirebaseUserId', id);
  }

  public getFirebaseUserId(): string {
    return this.cookieManager.getCookie('setFirebaseUserId');
  }

  public setPriceId = (data: string) => {
    this.cookieManager.setCookie('priceId', data);
  };

  public getPriceId(): string {
    return this.cookieManager.getCookie('priceId');
  }

  public setUserId = (data: string) => {
    const value = String(data || '').trim();
    if (!value) return;
    this.cookieManager.setCookie('userId', value);
    try {
      localStorage.setItem('userId', value);
    } catch (e) {
      /* ignore */
    }
  };

  public setUserEmail = (data: string) => {
    this.cookieManager.setCookie('email', data);
  };

  public getUserEmail(): string {
    return this.cookieManager.getCookie('email');
  }

  public setGProviderDetails = (data: any) => {
    this.cookieManager.setCookie('gProvider', data);
  };

  public getgProviderDetails(): string {
    return this.cookieManager.getCookie('gProvider');
  }

  public setisIntegratedWithGoogle = (data: any) => {
    const normalizedValue = data === true || data === 'true' ? 'true' : 'false';
    this.cookieManager.setCookie('isGoogleIntegrated', normalizedValue);
    this.setPendingGoogleIntegrationDisable(false);

    const syncLoggedInDataFlag = (raw: string | null): void => {
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          parsed.isGoogleIntegrated = normalizedValue;
          const serialized = JSON.stringify(parsed);
          this.cookieManager.setCookie('loggedInData', serialized);
          localStorage.setItem('loggedInData', serialized);
        }
      } catch (e) {
        /* ignore invalid JSON payload */
      }
    };

    syncLoggedInDataFlag(this.cookieManager.getCookie('loggedInData'));
    syncLoggedInDataFlag(localStorage.getItem('loggedInData'));
  };

  public getisIntegratedWithGoogle(): string {
    if (this.isPendingGoogleIntegrationDisable()) {
      return 'true';
    }

    const loggedInData = this.getLoggedInData();
    const loggedInFlag = loggedInData?.isGoogleIntegrated;

    if (typeof loggedInFlag === 'boolean') {
      const normalizedFlag = loggedInFlag ? 'true' : 'false';
      if (this.cookieManager.getCookie('isGoogleIntegrated') !== normalizedFlag) {
        this.cookieManager.setCookie('isGoogleIntegrated', normalizedFlag);
      }
      return normalizedFlag;
    }

    if (typeof loggedInFlag === 'string' && (loggedInFlag === 'true' || loggedInFlag === 'false')) {
      if (this.cookieManager.getCookie('isGoogleIntegrated') !== loggedInFlag) {
        this.cookieManager.setCookie('isGoogleIntegrated', loggedInFlag);
      }
      return loggedInFlag;
    }

    return this.cookieManager.getCookie('isGoogleIntegrated');
  }

  public isGoogleIntegrationRemoved(): boolean {
    return this.getisIntegratedWithGoogle() === 'false';
  }

  public setPendingGoogleIntegrationDisable = (isPending: any) => {
    const pendingFlag = isPending === true || isPending === 'true';
    if (pendingFlag) {
      this.cookieManager.setCookie(Constants.SESSION_KEYS.PENDING_GOOGLE_INTEGRATION_DISABLE, 'true');
      return;
    }
    this.cookieManager.clearCookie(Constants.SESSION_KEYS.PENDING_GOOGLE_INTEGRATION_DISABLE);
  };

  public isPendingGoogleIntegrationDisable(): boolean {
    return this.cookieManager.getCookie(Constants.SESSION_KEYS.PENDING_GOOGLE_INTEGRATION_DISABLE) === 'true';
  }

  public setLoggedInData = (data: any) => {
    localStorage.setItem('login', 'login');
    localStorage.setItem('loggedInData', JSON.stringify(data));
    this.cookieManager.setCookie('loggedInData', JSON.stringify(data));
    const extracted = this.extractUserIdFromObject(data);
    if (extracted) {
      this.cookieManager.setCookie('userId', extracted);
      try {
        localStorage.setItem('userId', extracted);
      } catch (e) {
        console.warn('SessionManager: Failed to save userId to localStorage in setLoggedInData:', e);
      }
    }
  };

  public getLoggedInData = () => {
    const cookieLoginData = this.cookieManager.getCookie('loggedInData');
    if (cookieLoginData) {
      try {
        return JSON.parse(cookieLoginData);
      } catch (e) {
        /* cookies are capped near 4KB, so a large payload comes back truncated — use the copy below */
      }
    }

    const localLoginData = localStorage.getItem('loggedInData');
    if (localLoginData) {
      try {
        return JSON.parse(localLoginData);
      } catch (e) {
        /* ignore corrupted copy */
      }
    }
    return null;
  };

  public getAnalyticsType = () => {
    const loggedInData = this.getLoggedInData();
    if (loggedInData && loggedInData.schoolData) {
      if (loggedInData.schoolData.isResearcher) {
        return 2;
      } else if (loggedInData.schoolData.isAnalyticsVisible) {
        return 1;
      }
      return 0;
    }
    return 0;
  };

  public setSubscriptionInfo = (data: any) => {
    this.cookieManager.setCookie('subscriptionInfo', JSON.stringify(data));
  };

  public setStudentLicenseEndDate = (data: any) => {
    this.cookieManager.setCookie('student_license_enddate', data);
  };

  public getStudentLicenseEndDate(): string {
    return this.cookieManager.getCookie('student_license_enddate');
  }

  public setStudentSubscriptionType = (data: any) => {
    this.cookieManager.setCookie('student_subscription_type', data);
  };

  public getStudentSubscriptionType(): string {
    return this.cookieManager.getCookie('student_subscription_type');
  }

  public getSubscriptionInfo = () => {
    return this.cookieManager.getCookie('subscriptionInfo') ? JSON.parse(this.cookieManager.getCookie('subscriptionInfo')) : null;
  };

  public setPlanInfo = (data: any) => {
    this.cookieManager.setCookie('planInfo', JSON.stringify(data));
  };

  public getplanInfo = () => {
    return this.cookieManager.getCookie('planInfo') ? JSON.parse(this.cookieManager.getCookie('planInfo')) : null;
  };

  public deleteLoggedInData() {
    return this.cookieManager.clearCookie('loggedInData');
  }

  public deletePriceId() {
    return this.cookieManager.clearCookie('priceId');
  }

  public setPlanId = (data: string) => {
    this.cookieManager.setCookie('planId', data);
    try {
      localStorage.setItem('planId', data);
    } catch (e) {
      /* ignore */
    }
  };

  public getPlanId(): string {
    const fromCookie = this.cookieManager.getCookie('planId');
    if (fromCookie) return fromCookie;
    let fromLocal = '';
    try {
      fromLocal = localStorage.getItem('planId') || '';
    } catch (err) {
      /* ignore */
    }
    if (fromLocal) {
      this.cookieManager.setCookie('planId', fromLocal);
      return fromLocal;
    }
    return '';
  }

  public setSubscriptionLocation = (data: string) => {
    this.cookieManager.setCookie('subscriptionLocation', data);
  };

  public getSubscriptionLocation(): string {
    return this.cookieManager.getCookie('subscriptionLocation');
  }

  public setIsSubcrpition = (data: string) => {
    this.cookieManager.setCookie('isSubcrpition', data);
  };

  public getIsSubcrpition(): string {
    return this.cookieManager.getCookie('isSubcrpition');
  }

  public setSocailSignupData = (data: any) => {
    localStorage.setItem('loggedInData', JSON.stringify(data));
    this.cookieManager.setCookie('socialSignupData', JSON.stringify(data));
  };

  public getGoogleDriveRedirectionalURL() {
    return localStorage.getItem('redirectionURL');
  }

  public getSocailSignupData = () => {
    return this.cookieManager.getCookie('socialSignupData') ? JSON.parse(this.cookieManager.getCookie('loggedInData')) : null;
  };

  public deleteSocailSignupData() {
    return this.cookieManager.clearCookie('socialSignupData');
  }

  public setIsLicenseCodeUsed = (data: string) => {
    this.cookieManager.setCookie('isLicenseCodeUsed', data);
  };

  public getIsLicenseCodeUsed(): string {
    return this.cookieManager.getCookie('isLicenseCodeUsed');
  }

  public setAppTutorial = (data: any) => {
    this.cookieManager.setCookie('isAppTutorial', data);
  };

  public getAppTutorial(): string {
    return this.cookieManager.getCookie('isAppTutorial');
  }

  public setSchoolAdminLoggedIn(userLoggedIn: boolean) {
    this.SchoolAdminLoggedIn.next(userLoggedIn);
  }

  public getSchoolAdminLoggedIn() {
    return this.SchoolAdminLoggedIn;
  }

  public setSiteAdminLoggedIn(userLoggedIn: boolean) {
    this.SiteAdminLoggedIn.next(userLoggedIn);
  }

  public getSiteAdminLoggedIn() {
    return this.SiteAdminLoggedIn;
  }

  public setStudentLoggedIn(userLoggedIn: boolean) {
    this.StudentLoggedIn.next(userLoggedIn);
  }

  public getStudentLoggedIn() {
    return this.StudentLoggedIn;
  }

  public setMicrosoftLogin() {
    return localStorage.setItem('MicrosoftLogin', 'login_with_microsoft');
  }

  public getMicrosoftLogin() {
    return localStorage.getItem('MicrosoftLogin');
  }

  public clearMicrosoftLogin() {
    return localStorage.removeItem('MicrosoftLogin');
  }

  public clearMsalInteraction() {
    return sessionStorage.removeItem('msal.interaction.status');
  }

  public clearSession() {
    return sessionStorage.clear();
  }
}
